import { useState } from 'react';
import { UserMinus, Plus, X } from 'lucide-react';
import { Card } from '@/components/Card';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { RevocationStatusPanel } from '@/components/RevocationStatusPanel';
import { post } from '@/lib/api';

interface Offboarding {
  id: string;
  employee_name: string;
  status: string;
  start_date: string;
  target_completion_date: string;
  progress: number;
  tasks: Array<{ id: string; title: string; completed: boolean; category: string }>;
}

export function OffboardingPage() {
  const { data, loading, refetch } = useApi<{ offboarding: Offboarding[] }>('offboarding');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [offboardForm, setOffboardForm] = useState({
    employee_id: '',
    employee_name: '',
    last_day: '',
    reason: 'voluntary_resignation',
  });

  async function handleCreate() {
    if (!offboardForm.employee_id || !offboardForm.last_day) {
      setCreateError('Employee ID and last day are required.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await post('offboarding', {
        employee_id: offboardForm.employee_id,
        employee_name: offboardForm.employee_name || undefined,
        last_day: offboardForm.last_day,
        reason: offboardForm.reason,
      });
      setShowCreate(false);
      setOffboardForm({ employee_id: '', employee_name: '', last_day: '', reason: 'voluntary_resignation' });
      refetch();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to start offboarding');
    } finally {
      setCreating(false);
    }
  }

  const inputClass = 'w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground';

  if (loading) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <UserMinus className="w-5 h-5 text-amber-500" /> Active Offboarding
        </h2>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600">
          <Plus className="w-4 h-4" /> Start Offboarding
        </button>
      </div>
      {(data?.offboarding ?? []).length === 0 ? (
        <Card><p className="text-sm text-muted-foreground">No active offboarding processes</p></Card>
      ) : (
        <div className="space-y-4">
          {(data?.offboarding ?? []).map(o => (
            <Card key={o.id}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{o.employee_name}</p>
                  <p className="text-xs text-muted-foreground">Last day: {o.target_completion_date}</p>
                </div>
                <StatusBadge status={o.status} />
              </div>
              <div className="w-full bg-surface-3 rounded-full h-2 mb-3">
                <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${o.progress}%` }} />
              </div>
              <div className="space-y-1">
                {o.tasks.map(t => (
                  <div key={t.id} className="flex items-center gap-2 text-xs">
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${t.completed ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'border-border'}`}>
                      {t.completed && '✓'}
                    </span>
                    <span className={t.completed ? 'text-muted-foreground line-through' : 'text-foreground'}>{t.title}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setExpandedId(expandedId === o.id ? null : o.id)}
                className="mt-3 text-xs text-red-400 hover:text-red-300"
              >
                {expandedId === o.id ? 'Hide' : 'Manage'} access revocation
              </button>
              {expandedId === o.id && (
                <div className="mt-3">
                  <RevocationStatusPanel offboardingId={o.id} />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create Offboarding Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Start Offboarding</h3>
              <button onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            {createError && <p className="text-red-400 text-sm">{createError}</p>}
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Employee ID *</label>
                <input value={offboardForm.employee_id} onChange={e => setOffboardForm(f => ({ ...f, employee_id: e.target.value }))} placeholder="Employee ID" className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Employee Name</label>
                <input value={offboardForm.employee_name} onChange={e => setOffboardForm(f => ({ ...f, employee_name: e.target.value }))} placeholder="John Smith" className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Last Working Day *</label>
                <input type="date" value={offboardForm.last_day} onChange={e => setOffboardForm(f => ({ ...f, last_day: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Reason</label>
                <select value={offboardForm.reason} onChange={e => setOffboardForm(f => ({ ...f, reason: e.target.value }))} className={inputClass}>
                  <option value="voluntary_resignation">Voluntary Resignation</option>
                  <option value="involuntary_termination">Involuntary Termination</option>
                  <option value="layoff">Layoff</option>
                  <option value="retirement">Retirement</option>
                  <option value="contract_end">Contract End</option>
                  <option value="mutual_agreement">Mutual Agreement</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowCreate(false)} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
              <button onClick={() => void handleCreate()} disabled={creating} className="px-4 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm disabled:opacity-50">
                {creating ? 'Starting...' : 'Start Offboarding'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
